import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Paper,
  Radio,
  RadioGroup,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useTheme } from '../../hooks/useTheme';
import {
  THEME_PREFERENCES,
  ThemePreference,
  UserPreferencesPayload,
} from '../../types/user';

/** How each theme choice is described to the user. */
const THEME_LABELS: Record<ThemePreference, string> = {
  light: 'Light',
  dark: 'Dark',
  system: 'Match my system',
};

const formatTimestamp = (value: string | null): string => {
  if (!value) {
    return 'Never';
  }
  const at = new Date(value);
  return Number.isNaN(at.getTime()) ? 'Unknown' : at.toLocaleString();
};

/** A positive number parsed from an input, or null when it is not usable. */
const positiveNumber = (raw: string): number | null => {
  const parsed = Number(raw.trim());
  return raw.trim() !== '' && Number.isFinite(parsed) && parsed > 0
    ? parsed
    : null;
};

/**
 * Self-service profile and preferences (issue #378).
 *
 * Preferences are stored server-side (#377), so a theme chosen here follows the
 * user to another browser or device instead of living in one machine's
 * localStorage.
 */
export const SettingsPanel: React.FC = () => {
  const { profile, roles, preferences, loaded, error, saving, savePreferences } =
    useCurrentUser();
  const { preference: activeTheme, setPreference } = useTheme();

  // Local draft so typing is not a write per keystroke. Re-seeded whenever the
  // stored preferences change, including after a save.
  const [draft, setDraft] = useState<UserPreferencesPayload>(preferences);
  const [saved, setSaved] = useState(false);

  // The numeric fields are held as raw text while being edited.
  //
  // A controlled number input that only accepts valid values cannot be cleared:
  // emptying it parses to nothing, the old value is written straight back, and
  // the next keystroke appends to it — so typing 120 over 50 yields 50120. The
  // text is the truth while editing; validity is decided on save.
  const [plannerText, setPlannerText] = useState({
    cruiseSpeedMs: String(preferences.planner.cruiseSpeedMs),
    altitudeM: String(preferences.planner.altitudeM),
  });

  useEffect(() => {
    setDraft(preferences);
    setPlannerText({
      cruiseSpeedMs: String(preferences.planner.cruiseSpeedMs),
      altitudeM: String(preferences.planner.altitudeM),
    });
  }, [preferences]);

  const cruiseSpeed = positiveNumber(plannerText.cruiseSpeedMs);
  const altitude = positiveNumber(plannerText.altitudeM);
  const plannerValid = cruiseSpeed !== null && altitude !== null;

  // The theme applies the moment it is picked rather than on save: a colour
  // scheme you cannot see until you commit is impossible to choose between.
  const onThemeChange = (next: ThemePreference) => {
    setDraft((current) => ({ ...current, theme: next }));
    setPreference(next);
  };

  const onPlannerChange = (
    field: keyof UserPreferencesPayload['planner'],
    raw: string
  ) => {
    setPlannerText((current) => ({ ...current, [field]: raw }));
  };

  const onSave = async () => {
    if (!plannerValid) {
      return;
    }
    // Spread the current document first. Saving is a full replace, so listing
    // only the fields this screen edits would reset every other one — the fleet
    // view the operator chose on the map has no control here and would be
    // silently thrown away on each save.
    const next: UserPreferencesPayload = {
      ...preferences,
      theme: draft.theme,
      planner: { cruiseSpeedMs: cruiseSpeed, altitudeM: altitude },
    };
    if (await savePreferences(next)) {
      setSaved(true);
    }
  };

  if (!loaded) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress aria-label="Loading your settings" />
      </Box>
    );
  }

  return (
    <Box p={{ xs: 2, sm: 3 }} maxWidth={720} mx="auto">
      <Typography variant="h5" component="h1" gutterBottom>
        Settings
      </Typography>

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}. Your changes may not be saved.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" component="h2" gutterBottom>
          Profile
        </Typography>
        {/* Read-only: the API has no endpoint to change a profile, which comes
            from the identity provider's claims on each login. */}
        <Stack spacing={1}>
          <Box>
            <Typography variant="caption" color="text.secondary" component="div">
              Name
            </Typography>
            <Typography>{profile?.display_name || 'Not set'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" component="div">
              Email
            </Typography>
            <Typography>{profile?.email || 'Not set'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" component="div">
              Roles
            </Typography>
            {roles.length === 0 ? (
              <Typography>None</Typography>
            ) : (
              <Stack direction="row" spacing={1} mt={0.5}>
                {roles.map((role) => (
                  <Chip key={role} label={role} size="small" />
                ))}
              </Stack>
            )}
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" component="div">
              Last sign-in
            </Typography>
            <Typography>{formatTimestamp(profile?.last_login_at ?? null)}</Typography>
          </Box>
        </Stack>
        <Typography variant="caption" color="text.secondary" display="block" mt={2}>
          Your name and email come from your sign-in provider and change there.
        </Typography>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <FormControl>
          <FormLabel id="theme-label">
            <Typography variant="h6" component="h2">
              Appearance
            </Typography>
          </FormLabel>
          <RadioGroup
            aria-labelledby="theme-label"
            name="theme"
            value={activeTheme}
            onChange={(event) =>
              onThemeChange(event.target.value as ThemePreference)
            }
          >
            {THEME_PREFERENCES.map((option) => (
              <FormControlLabel
                key={option}
                value={option}
                control={<Radio />}
                label={THEME_LABELS[option]}
              />
            ))}
          </RadioGroup>
        </FormControl>
        <Typography variant="caption" color="text.secondary" display="block">
          &ldquo;Match my system&rdquo; follows your device&rsquo;s light or dark
          setting as it changes.
        </Typography>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" component="h2" gutterBottom>
          Mission planner defaults
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" mb={2}>
          Used when a new mission starts. Existing missions keep their own values.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label="Cruise speed (m/s)"
            type="number"
            size="small"
            value={plannerText.cruiseSpeedMs}
            onChange={(event) =>
              onPlannerChange('cruiseSpeedMs', event.target.value)
            }
            error={cruiseSpeed === null}
            helperText={cruiseSpeed === null ? 'Enter a speed above zero' : ' '}
            inputProps={{ min: 1, step: 1, 'aria-label': 'Cruise speed in metres per second' }}
          />
          <TextField
            label="Altitude (m)"
            type="number"
            size="small"
            value={plannerText.altitudeM}
            onChange={(event) => onPlannerChange('altitudeM', event.target.value)}
            error={altitude === null}
            helperText={altitude === null ? 'Enter an altitude above zero' : ' '}
            inputProps={{ min: 1, step: 1, 'aria-label': 'Altitude in metres' }}
          />
        </Stack>
      </Paper>

      <Divider sx={{ mb: 2 }} />

      <Stack direction="row" spacing={2} alignItems="center">
        <Button
          variant="contained"
          onClick={onSave}
          disabled={saving || !plannerValid}
        >
          {saving ? 'Saving…' : 'Save preferences'}
        </Button>
        <Typography variant="caption" color="text.secondary">
          Saved to your account, so they follow you to any device.
        </Typography>
      </Stack>

      <Snackbar
        open={saved}
        autoHideDuration={4000}
        onClose={() => setSaved(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSaved(false)}>
          Preferences saved
        </Alert>
      </Snackbar>
    </Box>
  );
};
